/**
 * The one place page coordinates are turned between rotations in the browser (Issue #2445).
 *
 * A plan page can be displayed at four orientations: 0°, 90°, 180° and 270° clockwise. Data attached
 * to a page (region boxes, OCR tokens, highlight rectangles, user selections) is always expressed in
 * some orientation of that page, called its frame. Whenever data captured in one frame is shown in
 * another, its coordinates have to be turned by the difference. Each feature used to carry its own
 * copy of the turning formulas, with its own axis order, and the copies drifted apart.
 *
 * Every conversion here is built on a single primitive, `rotatePoint`. Boxes are turned by turning
 * their corners and taking the enclosing bounds, so no function has a second set of per-axis
 * formulas that could disagree with the first.
 *
 * Conventions, shared with the server library `PageGeometry.java`:
 * - The origin is the top-left corner of the frame; x grows to the right and y grows downward.
 * - Rotation is clockwise, the direction of the PDF `/Rotate` entry and of `PDFPageProxy.rotate`.
 * - A frame has a width and a height. Normalized data uses the same number for both (1 or 1000).
 *   Turning by 90° or 270° swaps them.
 *
 * The two languages are held to the same answers by a shared table of worked examples,
 * `src/test/resources/page-geometry/rotation-vectors.json`, which both test suites read.
 *
 * This module is pure (no Angular, no DOM), so it can be used anywhere and tested without a component.
 */

/** A page's clockwise display rotation. */
export type PageRotation = 0 | 90 | 180 | 270;

/** A point in a frame. */
export interface GeometryPoint {
  x: number;
  y: number;
}

/** The size of a frame. */
export interface FrameSize {
  width: number;
  height: number;
}

/** A rectangle given by its top-left corner and its size. */
export interface GeometryRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Extent of the 0–1000 normalized range used by region boxes and OCR tokens. */
export const PERMILLE = 1000;

/** The frame of data normalized to 0–1 on both axes. */
export const UNIT_FRAME: FrameSize = Object.freeze({ width: 1, height: 1 });

/** The frame of data normalized to 0–1000 on both axes. */
export const PERMILLE_FRAME: FrameSize = Object.freeze({ width: PERMILLE, height: PERMILLE });

/**
 * Reduces any angle to one of the four supported rotations.
 *
 * The browser reads rotations from PDF.js, which only reports multiples of 90°, so a stray value
 * is rounded to the nearest quarter turn rather than rejected. A missing value is 0°.
 */
export function normalizeRotation(degrees: number | null | undefined): PageRotation {
  const rotation = (((Math.round((degrees ?? 0) / 90) * 90) % 360) + 360) % 360;
  return rotation as PageRotation;
}

/**
 * The clockwise turn that carries data captured at `fromRotation` onto a page now displayed at
 * `toRotation`.
 */
export function rotationDelta(
  fromRotation: number | null | undefined,
  toRotation: number | null | undefined
): PageRotation {
  return normalizeRotation(normalizeRotation(toRotation) - normalizeRotation(fromRotation));
}

/** The size of a frame after it is turned clockwise. */
export function rotateFrame(frame: FrameSize, clockwiseDegrees: number): FrameSize {
  const rotation = normalizeRotation(clockwiseDegrees);
  return rotation === 90 || rotation === 270 ? { width: frame.height, height: frame.width } : { ...frame };
}

/**
 * Turns one point clockwise. This is the primitive every other conversion uses.
 *
 * Turning a frame 90° clockwise carries its top-left corner to the top-right, so a point (x, y)
 * lands at (height − y, x) in the turned frame.
 *
 * @param point the point, in a frame of the given size
 * @param frame the size of the frame the point is expressed in, before the turn
 * @param clockwiseDegrees the turn to apply
 */
export function rotatePoint(point: GeometryPoint, frame: FrameSize, clockwiseDegrees: number): GeometryPoint {
  const { x, y } = point;
  switch (normalizeRotation(clockwiseDegrees)) {
    case 90:
      return { x: frame.height - y, y: x };
    case 180:
      return { x: frame.width - x, y: frame.height - y };
    case 270:
      return { x: y, y: frame.width - x };
    default:
      return { x, y };
  }
}

/** Turns every vertex of a polygon. Vertex order is preserved. */
export function rotatePolygon(points: GeometryPoint[], frame: FrameSize, clockwiseDegrees: number): GeometryPoint[] {
  return points.map(point => rotatePoint(point, frame, clockwiseDegrees));
}

/** The smallest rectangle containing every point. An empty set yields an empty rectangle. */
export function boundsOfPoints(points: GeometryPoint[]): GeometryRect {
  if (!points.length) return { x: 0, y: 0, width: 0, height: 0 };
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const { x, y } of points) {
    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x);
    maxY = Math.max(maxY, y);
  }
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}

/**
 * Turns a rectangle by turning all four corners and taking their enclosing bounds.
 *
 * Works in any frame: 0–1 fractions, 0–1000 fractions, PDF points or pixels. Turning only two
 * opposite corners gives a rectangle whose "top-left" is no longer the top-left, which is the
 * mistake that produced swapped highlights.
 *
 * @param rect the rectangle, in a frame of the given size
 * @param frame the size of the frame the rectangle was measured in
 */
export function rotateRect(rect: GeometryRect, frame: FrameSize, clockwiseDegrees: number): GeometryRect {
  const rotation = normalizeRotation(clockwiseDegrees);
  const bounds = boundsOfPoints(
    rotatePolygon(
      [
        { x: rect.x, y: rect.y },
        { x: rect.x + rect.width, y: rect.y },
        { x: rect.x + rect.width, y: rect.y + rect.height },
        { x: rect.x, y: rect.y + rect.height },
      ],
      frame,
      rotation
    )
  );
  // The position comes from the turned corners. The size is the original size, with its sides
  // swapped on a quarter turn: taking it from the corners instead would be the same number less a
  // rounding error, and a rectangle turned four times would no longer be exactly itself.
  const quarter = rotation === 90 || rotation === 270;
  return {
    x: bounds.x,
    y: bounds.y,
    width: quarter ? rect.height : rect.width,
    height: quarter ? rect.width : rect.height,
  };
}

/**
 * Turns a box stored as `[y_min, x_min, y_max, x_max]` in the 0–1000 range. This is the layout the
 * region hierarchy and the OCR tokens use. The result is in the same order.
 */
export function rotatePermilleYxBox(yxBox: readonly number[], clockwiseDegrees: number): number[] {
  if (yxBox.length !== 4) {
    throw new Error('A box needs exactly four values: y_min, x_min, y_max, x_max');
  }
  const [yMin, xMin, yMax, xMax] = yxBox;
  const turned = rotateRect(
    { x: xMin, y: yMin, width: xMax - xMin, height: yMax - yMin },
    PERMILLE_FRAME,
    clockwiseDegrees
  );
  return [
    Math.round(turned.y),
    Math.round(turned.x),
    Math.round(turned.y + turned.height),
    Math.round(turned.x + turned.width),
  ];
}

/**
 * Converts a `[y_min, x_min, y_max, x_max]` 0–1000 box to a rectangle in the units of the page as
 * displayed, turning it first when it was expressed for a different rotation.
 *
 * @param yxBox the box as received
 * @param boxRotation the page rotation the box is expressed for; `undefined` when the sender did
 *     not say, in which case the box is taken to match the page as displayed
 * @param displayRotation the rotation the page is rendered at now
 * @param displayedSize the size of the page as rendered now, in the units wanted
 */
export function permilleYxBoxToDisplayRect(
  yxBox: readonly number[],
  boxRotation: number | null | undefined,
  displayRotation: number,
  displayedSize: FrameSize
): GeometryRect {
  const delta = boxRotation === null || boxRotation === undefined ? 0 : rotationDelta(boxRotation, displayRotation);
  const [yMin, xMin, yMax, xMax] = rotatePermilleYxBox(yxBox, delta);
  return {
    x: (xMin / PERMILLE) * displayedSize.width,
    y: (yMin / PERMILLE) * displayedSize.height,
    width: ((xMax - xMin) / PERMILLE) * displayedSize.width,
    height: ((yMax - yMin) / PERMILLE) * displayedSize.height,
  };
}

/**
 * Turns a rectangle measured in units of the displayed page (PDF points or pixels) from the
 * rotation it was measured at to the rotation the page is displayed at now. This is the layout
 * highlight links use.
 *
 * @param rect the rectangle as measured
 * @param rectRotation the page rotation the rectangle was measured at
 * @param displayRotation the rotation the page is rendered at now
 * @param displayedSize the size of the page as rendered now, in the same units as the rectangle
 */
export function displayRectToRotation(
  rect: GeometryRect,
  rectRotation: number,
  displayRotation: number,
  displayedSize: FrameSize
): GeometryRect {
  const delta = rotationDelta(rectRotation, displayRotation);
  if (delta === 0) return { ...rect };
  // The frame the rectangle was measured in is the displayed page turned back by the difference.
  const measuredFrame = rotateFrame(displayedSize, -delta);
  return rotateRect(rect, measuredFrame, delta);
}
